import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Download, X, MailOpen, MousePointerClick, BellOff } from "lucide-react";

const fmt = (iso) => (iso ? iso.replace("T", " ").slice(0, 16) : "—");

export default function BroadcastRecipientsPanel({ broadcast, onClose }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    setData(null);
    api.get(`/admin/broadcast/${broadcast.id}/recipients`).then(({ data }) => setData(data)).catch(() => toast.error("Could not load recipients"));
  }, [broadcast.id]);

  const rows = (data?.items || []).filter((r) => filter === "all" || (filter === "opened" && r.opens) || (filter === "clicked" && r.clicks) || (filter === "unopened" && !r.opens));
  const csvUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/broadcast/${broadcast.id}/recipients.csv`;

  return (
    <div className="bg-white border border-[#264839] p-6" data-testid="broadcast-recipients-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="tracking-overline text-[10px] text-[#264839]">Per-recipient report</div>
          <h3 className="font-serif text-xl text-[#11211A] mt-0.5" data-testid="recipients-subject">{broadcast.subject}</h3>
          <div className="text-xs text-[#7D8C85] mt-1">{broadcast.audience_label} · sent {fmt(broadcast.sent_at)}{data ? ` · ${data.count} recipients · ${data.opened} opened · ${data.clicked} clicked` : ""}</div>
        </div>
        <div className="flex items-center gap-2">
          <a href={csvUrl} className="border border-[#264839] text-[#264839] px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#264839] hover:text-white transition inline-flex items-center gap-1.5" data-testid="recipients-csv-download">
            <Download className="w-3.5 h-3.5" /> Download CSV
          </a>
          <button type="button" onClick={onClose} className="text-[#7D8C85] hover:text-[#11211A] p-2" aria-label="Close report" data-testid="recipients-close"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex gap-2 mb-3">
        {[["all", "All"], ["opened", "Opened"], ["clicked", "Clicked"], ["unopened", "Not opened"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} className={`text-[10px] uppercase tracking-wider px-2.5 py-1 border transition ${filter === k ? "bg-[#264839] text-white border-[#264839]" : "border-[#C4CFC9] text-[#4A5550] hover:border-[#264839]"}`} data-testid={`recipients-filter-${k}`}>{label}</button>
        ))}
      </div>
      {!data ? <div className="text-sm text-[#7D8C85]">Loading…</div> : rows.length === 0 ? <div className="text-sm text-[#7D8C85]" data-testid="recipients-empty">No recipients match this filter.</div> : (
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm" data-testid="recipients-table">
            <thead className="text-left text-[10px] uppercase tracking-widest text-[#7D8C85] border-b border-[#E8ECE9] sticky top-0 bg-white">
              <tr><th className="py-2 pr-3">Recipient</th><th className="py-2 px-3">Sent</th><th className="py-2 px-3"><MailOpen className="w-3 h-3 inline -mt-0.5" /> Opened</th><th className="py-2 px-3"><MousePointerClick className="w-3 h-3 inline -mt-0.5" /> Clicked</th><th className="py-2 pl-3">Last link</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.rid} className="border-b border-[#F0EFEA]" data-testid={`recipient-row-${r.rid}`}>
                  <td className="py-2 pr-3">
                    <div className="text-[#11211A]">{r.email}</div>
                    <div className="text-[11px] text-[#7D8C85]">{r.company}{r.is_unclaimed ? " · unclaimed" : ""}{r.unsubscribed ? <span className="text-[#B35C4A] ml-1 inline-flex items-center gap-0.5"><BellOff className="w-3 h-3" /> unsubscribed</span> : null}{r.bounced ? <span className="text-[#B35C4A] ml-1">· bounced</span> : null}</div>
                  </td>
                  <td className="py-2 px-3 text-[#7D8C85] tabular-nums whitespace-nowrap">{fmt(r.sent_at)}</td>
                  <td className="py-2 px-3 tabular-nums whitespace-nowrap">{r.opens ? <span className="text-[#264839]">{fmt(r.opened_at)} <span className="text-[#7D8C85] text-xs">×{r.opens}</span></span> : <span className="text-[#C4CFC9]">—</span>}</td>
                  <td className="py-2 px-3 tabular-nums whitespace-nowrap">{r.clicks ? <span className="text-[#B8860B]">{fmt(r.clicked_at)} <span className="text-[#7D8C85] text-xs">×{r.clicks}</span></span> : <span className="text-[#C4CFC9]">—</span>}</td>
                  <td className="py-2 pl-3 text-xs text-[#7D8C85] max-w-[260px] truncate" title={r.last_click_url || ""}>{r.last_click_url || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
